import React, { useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  NativeSelect,
  FormControl,
} from "@material-ui/core";
import { withStyles } from "@material-ui/styles";
import { withRouter } from "react-router-dom";
import { compose } from "recompose";

import AppToolBar from "../../../../common/AppToolBar";
import OrganizerCommonHeader from "../OrganizerCommonHeader";
import Breadcrumbs from "../../../../common/Breadcrumbs";
import ApplicationUtil from "../../../../../utils/ApplicationUtil";
import { useState } from "react";
import RivalryIndexTable from "../../../../common/Graphs/RivalryIndexTable";
import EngagementDoughnutChart from "../../../../common/Graphs/EnagegmentDoughnutGraph";
import Group from "@material-ui/icons/Group";
import BarChart from "@material-ui/icons/BarChart"; // New for Retention
import TrackChanges from "@material-ui/icons/TrackChanges"; // New for Quality (Target)
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import {
  getDailyActivityDetails,
  getEngagementRateDetails,
  getKnowledgeSessions,
  getQualityOfEngagement,
  getRivalryIndexData,
  getWeeklyStatistics,
} from "../../../../../apis/graphService";
import RetentionGraph from "../../../../common/Graphs/RetentionGraph";
import moment from "moment";
import EngagementRateGraph from "../../../../common/Graphs/EngagementRateGraph";
import { getProgramEventList } from "../../../../../apis/eventService";
import EventIcon from "@material-ui/icons/Event";
import { FilterSVGIcon } from "../../../../common/SvgIcon";
import DirectionsRunSharpIcon from "@material-ui/icons/DirectionsRunSharp";
import BarGraphView from "../../../../common/Graphs/BarGraphView";
import KnowledgeSessionCard from "../../../../common/Graphs/KnowledgeSessionCard";

function HRDashboardView(props) {
  const { classes, navigatedFromCalendarProgram } = props;
  const [filters, setFilters] = useState("");
  const [dropdownOptionValues, setDropdownOptionValues] = useState([]);
  const [isProgram, setIsProgram] = useState(false);

  // Engagement Rate States
  const [engagementRateValue, setEngagementRateValue] = useState(0);

  // Daily Activity States
  const [dailyActivityData, setDailyActivityData] = useState([]);
  const [totalDailyActivityValue, setTotalDailyActivityValue] = useState(0);
  const [challengeParameters, setSetChallengeParameters] = useState("STEPS");

  // Rivalry Index States
  const [rivalryIndexData, setRivalryIndexData] = useState([]);
  const [rivalryMeanValue, setRivalryMeanValue] = useState("0");

  // Retention States
  const [retentionRateData, setRetentionRateData] = useState([]);
  const [retentionPercentageValue, setRetentionPercentageValue] = useState("0");

  // Quality Engagement States
  const [qualityEngagementValue, setQualityEngagementValue] = useState("0");
  const [qualityBreakdown, setQualityBreakdown] = useState(null);

  //knowledge session data
  const [knowledgeSessionData, setKnowledgeSessionData] = useState([]);

  // Filter State
  const [timeFilter, setTimeFilter] = useState("OVERALL");
  const [eventMeta, setEventMeta] = useState({
    startDate: null,
    endDate: null,
    isEnded: false,
  });

  // --- Helper: Date Calculation Engine ---
  const calculateDateRange = (filter, startStr, endStr) => {
    if (!startStr || !endStr) return { from: null, to: null };

    const eventStart = moment(startStr, "YYYY-MM-DD").startOf("day");
    const eventEnd = moment(endStr, "YYYY-MM-DD").endOf("day");
    const today = moment().endOf("day");

    // Determine Anchor: If event is ended, use Event End; otherwise use Today.
    const isEnded = moment().isAfter(eventEnd);
    const anchorDate = isEnded ? eventEnd.clone() : moment();

    let fromDateTime, toDateTime;

    switch (filter) {
      case "OVERALL":
        // If event is ended, sent startStr and endStr (full event duration)
        if (isEnded) {
          fromDateTime = eventStart.clone();
          toDateTime = eventEnd.clone();
        }
        // If event is ongoing (not ended), sent startStrDate and today's date
        else {
          fromDateTime = eventStart.clone();
          toDateTime = today.clone();
        }
        break;

      case "TODAY":
        // For 'Today', we just want the anchor date day.
        // But we still apply the safety check: don't go past Event End.
        fromDateTime = anchorDate.clone().startOf("day");
        toDateTime = anchorDate.clone().endOf("day");

        // Safety: If 'Today' is somehow past EventEnd (rare if logic is correct), cap it.
        if (toDateTime.isAfter(eventEnd)) toDateTime = eventEnd.clone();
        break;

      case "THIS_MONTH":
        // The 'diff' in moment is tricky for month blocks. A more precise approach:
        // 1. Calculate the difference in calendar years * 12 + difference in calendar months.
        const yearDiff = anchorDate.year() - eventStart.year();
        const monthDiff = anchorDate.month() - eventStart.month();
        const totalMonthsElapsed = yearDiff * 12 + monthDiff;

        // 2. Check if the 'anchorDate' has passed the day-of-month of 'eventStart'.

        let monthIndex = totalMonthsElapsed;
        if (anchorDate.date() < eventStart.date()) {
          monthIndex -= 1;
        }

        // Ensure index doesn't go negative if the event hasn't started yet (though startStr/endStr check should handle this)
        monthIndex = Math.max(0, monthIndex);

        // 1. Determine the start of the current month block based on eventStart
        const blockMonthStart = eventStart
          .clone()
          .add(monthIndex, "months")
          .startOf("day");

        // 2. Determine the end of the current month block.
        const blockMonthEnd = blockMonthStart
          .clone()
          .add(1, "month")
          .subtract(1, "second")
          .endOf("day");

        // The start date is simply the block start date
        fromDateTime = blockMonthStart;

        // 3. Logic for End Date (Apply the 2 conditions via Min function):
        let validEnd = blockMonthEnd.clone();

        // Condition 1: Cap at Today
        if (validEnd.isAfter(today)) {
          validEnd = today.clone();
        }

        // Condition 2: Cap at Event End Date
        if (validEnd.isAfter(eventEnd)) {
          validEnd = eventEnd.clone();
        }

        toDateTime = validEnd;
        break;

      case "THIS_WEEK":
        const diffDays = anchorDate.diff(eventStart, "days");
        const weekIndex = Math.floor(diffDays / 7);

        const blockStart = eventStart.clone().add(weekIndex * 7, "days");
        const blockEnd = blockStart.clone().add(6, "days");

        fromDateTime = blockStart.startOf("day");

        // Apply similar safety caps to Week view if needed,
        // though your requirement specifically highlighted Month.
        let weekEndCalc = blockEnd.endOf("day");

        // Cap at Today
        if (weekEndCalc.isAfter(today)) weekEndCalc = today.clone();
        // Cap at Event End
        if (weekEndCalc.isAfter(eventEnd)) weekEndCalc = eventEnd.clone();

        toDateTime = weekEndCalc;
        break;

      default:
        fromDateTime = moment().startOf("day");
        toDateTime = moment().endOf("day");
    }

    return {
      fromDateTime: fromDateTime.format("YYYY-MM-DD HH:mm:ss"),
      toDateTime: toDateTime.format("YYYY-MM-DD HH:mm:ss"),
    };
  };

  const HIDE_RIVALRY_TYPES = [
    "ZERO_SUGAR",
    "HYDRATION",
    "YOGA",
    "NO_UNHEALTHY_EATING",
  ];

  const shouldHideRivalry = HIDE_RIVALRY_TYPES.includes(
    filters?.programChallengeType,
  );

  // --- Initial Data Fetch (Runs Once) ---
  useEffect(() => {
    const fetchList = async () => {
      const supportEventData = ApplicationUtil.getSupportsEventsDetails();
      const selectedProgramDetails =
        ApplicationUtil.getSupportsProgramDetails();

      if (!navigatedFromCalendarProgram) {
        await getProgramEventList({
          programId: selectedProgramDetails?.id,
        }).then(({ data }) => {
          let eventDTO = data?.eventDTO;
          let programId = data?.id;
          let programSessions = data?.programSessions;

          let programChallenges = data?.programChallenges ?? [];
          let mergedList = [...(eventDTO || []), ...(programChallenges || [])];
          setDropdownOptionValues(mergedList);
          setFilters(mergedList[0]);

          if (programSessions && programSessions.length > 0) {
            getKnowledgeSessionData(programId);
          } else {
            setKnowledgeSessionData([]);
          }
        });
      } else {
        setDropdownOptionValues(supportEventData ? [supportEventData] : []);
        setFilters([supportEventData][0]);
      }
    };
    fetchList();
  }, []);

  const getKnowledgeSessionData = async (programId) => {
    try {
      const { data } = await getKnowledgeSessions(programId);
      setKnowledgeSessionData(data);
    } catch (error) {
      console.error("Error fetching knowledge session data:", error);
    }
  };

  useEffect(() => {
    const fetchGraphData = async () => {
      if (filters && !filters?.programId) {
        const startStr = filters?.localStartDate;
        const endStr = filters?.localEndDate;
        const hasEventEnded = moment().isAfter(moment(endStr, "YYYY-MM-DD"));

        setEventMeta({
          startDate: startStr,
          endDate: endStr,
          isEnded: hasEventEnded,
        });

        const endDateToSend = hasEventEnded
          ? endStr
          : moment().format("YYYY-MM-DD");
        const isProgramChallenge = !!filters?.programId;
        setIsProgram(isProgramChallenge);

        try {
          // Retention Rate Data Fetch
          const { data } = await getWeeklyStatistics(
            filters?.id,
            startStr,
            endDateToSend,
            isProgramChallenge,
          );
          setRetentionRateData(data?.engagementRetentionStatsDtos);
          const percentage = data?.totalPercentage;
          setRetentionPercentageValue(
            percentage === 0 ? "0" : percentage.toFixed(2),
          );

          // Daily Activity Data Fetch
          const dailyActivityData = await getDailyActivityDetails(
            filters?.id,
            startStr,
            endDateToSend,
          );

          const activities = Array.isArray(
            dailyActivityData?.data?.dayWiseStatsDTOs,
          )
            ? dailyActivityData.data?.dayWiseStatsDTOs
            : [];

          const processedActivities =
            dailyActivityData?.data?.challengeParameters === "DISTANCE"
              ? activities.map((item) => ({
                  ...item,
                  // Assuming the value field is called 'value' or 'total'
                  // Replace 'value' with your actual data key (e.g., item.distance)
                  totalValue: item.totalValue ? item.totalValue / 1000 : 0,
                }))
              : activities;

          setDailyActivityData(processedActivities);
          setSetChallengeParameters(
            dailyActivityData?.data?.challengeParameters || "STEPS",
          );

          const total = processedActivities.reduce((sum, item) => {
            // Ensure totalValue is treated as a number
            return sum + (Number(item.totalValue) || 0);
          }, 0);
          setTotalDailyActivityValue(total || 0);

          // Rivalry Index Data Fetch
          const rivalryData = await getRivalryIndexData(filters?.id);
          setRivalryIndexData(rivalryData || []);
          setRivalryMeanValue(
            rivalryData?.data?.averageRivalryIndex.toFixed(2) || "0",
          );
        } catch (error) {
          console.error("Error fetching static data:", error);
          setRivalryIndexData([]);
          setRivalryMeanValue("0");
        }
      } else if (filters && filters?.programId) {
        setRivalryIndexData([]);
        setRivalryMeanValue("0");

        // Define the date formats
        const inputFormat = "DD-MM-YYYY HH:mm:ss";
        const outputFormat = "YYYY-MM-DD HH:mm:ss";

        const rawStartDateStr = filters?.challengeStartDate;
        const rawEndDateStr = filters?.challengeEndDate;

        const startDateMoment = rawStartDateStr
          ? moment(rawStartDateStr, inputFormat)
          : null;
        const endDateMoment = rawEndDateStr
          ? moment(rawEndDateStr, inputFormat)
          : null;

        const startStr = startDateMoment
          ? startDateMoment.format(outputFormat)
          : null;

        const endStr = endDateMoment
          ? endDateMoment.format(outputFormat)
          : null;

        const hasEventEnded = endDateMoment
          ? moment().isAfter(endDateMoment)
          : false;

        setEventMeta({
          startDate: startStr,
          endDate: endStr,
          isEnded: hasEventEnded,
        });

        try {
          const endDateToSend = hasEventEnded
            ? endStr
            : moment().format("YYYY-MM-DD HH:mm:ss");

          const isProgramChallenge = !!filters?.programId;
          setIsProgram(isProgramChallenge);

          const { data } = await getWeeklyStatistics(
            filters?.id,
            startStr,
            endDateToSend,
            isProgramChallenge,
          );

          setRetentionRateData(data?.engagementRetentionStatsDtos);
          const percentage = data?.totalPercentage;
          setRetentionPercentageValue(
            percentage === 0 ? "0" : percentage.toFixed(2),
          );
        } catch (error) {
          setRetentionRateData([]);
          setRetentionPercentageValue("0");
        }
      }
    };
    fetchGraphData();
  }, [filters]);

  // --- Filter Effect (Runs when Dropdown or EventMeta changes) ---
  useEffect(() => {
    const fetchDataWithFilter = async () => {
      if (!eventMeta.startDate || !eventMeta.endDate) return;

      const { fromDateTime, toDateTime } = calculateDateRange(
        timeFilter,
        eventMeta.startDate,
        eventMeta.endDate,
      );

      // Defensive Check for handling event which are in future
      if (new Date(fromDateTime) > new Date(toDateTime)) {
        setEngagementRateValue(0);
        return;
      }
      const isProgramChallenge = !!filters?.programId;
      setIsProgram(isProgramChallenge);

      /* ---------------- Quality of Engagement ---------------- */
      if (timeFilter === "TODAY") {
        // 👇 Explicitly clear Quality data for TODAY
        setQualityEngagementValue("0");
        setQualityBreakdown(null);
      } else {
        try {
          const { data } = await getQualityOfEngagement(
            filters?.id,
            fromDateTime,
            toDateTime,
            isProgramChallenge,
          );
          const score = data?.averageEngagement?.toFixed(2) || "0";
          setQualityEngagementValue(score);
          setQualityBreakdown(data?.engagementChartDTOs || null);
        } catch (error) {
          console.error("Error fetching quality metrics:", error);
          setQualityEngagementValue("0");
          setQualityBreakdown(null);
        }
      }

      /* ---------------- Engagement Rate (Always fetch) ---------------- */
      try {
        const { data } = await getEngagementRateDetails(
          filters?.id,
          fromDateTime,
          toDateTime,
          isProgramChallenge,
        );
        const rate = data?.engagementPercentage || 0;
        setEngagementRateValue(rate);
      } catch (error) {
        console.error("Error fetching engagement rate:", error);
        setEngagementRateValue("0");
      }
    };

    fetchDataWithFilter();
  }, [timeFilter, filters, eventMeta]);

  const metrics = [
    {
      id: "engagement",
      label: "Engagement Rate",
      value: engagementRateValue ? `${engagementRateValue}%` : "0%",
      //   meta: "↑12% from last week",
      className: classes.infoCard,
      icon: Group, // Correct icon, but needs styling
      color: "#9C27B0", // Info/Purple color
      bgColor: "#E1BEE7", // Light purple background tone (used for icon circle)
    },
    {
      id: "retention",
      label: "Retention Rate",
      value: retentionPercentageValue ? `${retentionPercentageValue}%` : "0",
      //   meta: "↓3% from last week",
      className: classes.primaryCard,
      icon: BarChart, // Changed from TrendingUp
      color: "#2196F3", // Primary/Blue color
      bgColor: "#BBDEFB", // Light blue background tone
    },
    {
      id: "quality",
      label: "Quality of Engagement",
      value: qualityEngagementValue ? `${qualityEngagementValue}%` : "0",
      className: classes.successCard,
      icon: TrackChanges, // Changed from CheckCircleOutline
      color: "#4CAF50", // Success/Green color
      bgColor: "#C8E6C9", // Light green background tone
    },
    {
      id: "rivalry",
      label: "Mean Rivalry Index",
      value: rivalryMeanValue ? rivalryMeanValue : "0",
      //   meta: "team events only",
      className: classes.warningCard,
      icon: ErrorOutline, // Changed from Gavel
      color: "#a7c825ff", // Warning/Orange color
      bgColor: "#E0FF60", // Light yellow background tone
    },
  ];

  const visibleMetrics = metrics.filter(
    (m) => !(shouldHideRivalry && m.id === "rivalry"),
  );

  const handleFilterChange = (event) => {
    setTimeFilter(event.target.value);
  };

  const handleChange = (e) => {
    const selectedId = e.target.value;
    const selectedItem = dropdownOptionValues.find(
      (item) => item.id.toString() === selectedId.toString(),
    );
    setFilters(selectedItem);
  };

  return (
    <div>
      <AppToolBar>
        <OrganizerCommonHeader title={"HR Dashboard"} />
        <div style={{ width: 35 }} />
      </AppToolBar>

      <div
        className={classes.mainContainer}
        style={{ marginTop: !navigatedFromCalendarProgram ? "60px" : "" }}
      >
        {!navigatedFromCalendarProgram && (
          <div className={classes.breadcrumbs_container}>
            <div className="breadcrumbs">
              <Breadcrumbs />
            </div>
          </div>
        )}

        <Grid container spacing={2} className={classes.dropdownStyling}>
          <Grid>
            <Typography variant="subtitle2" gutterBottom>
              Track engagement, retention, and performance metrics
            </Typography>
            <Box display="flex" alignItems="center" my={1}>
              <Box display="flex" alignItems="center" mr={2}>
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: "#673AB7", // Purple
                    marginRight: 8,
                  }}
                />
                <Typography variant="caption" style={{ color: "#616161" }}>
                  <strong style={{ color: "black" }}>Individual Events</strong>{" "}
                  : 3 metrics displayed
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: "#03A9F4", // Cyan/Light Blue
                    marginRight: 8,
                  }}
                />
                <Typography variant="caption" style={{ color: "#616161" }}>
                  <strong style={{ color: "black" }}>Team Events</strong> : All
                  4 metrics displayed
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid>
            <Grid
              component="span"
              style={{ display: "inline-flex", alignItems: "center" }}
            >
              <FilterSVGIcon
                style={{
                  color: "#09456C",
                  fontSize: 18,
                  paddingRight: 5,
                }}
              />
              <FormControl className={classes.formControl}>
                <NativeSelect
                  className={classes.selectActivity}
                  onChange={handleChange}
                >
                  {dropdownOptionValues?.map((value, index) => (
                    <option key={value.id} value={value.id}>
                      {value.name}
                    </option>
                  ))}
                </NativeSelect>
              </FormControl>
            </Grid>
            <Grid
              component="span"
              style={{ display: "inline-flex", alignItems: "center" }}
            >
              <EventIcon
                style={{
                  color: "#09456C",
                  fontSize: 20,
                  paddingRight: 5,
                }}
              />
              <FormControl className={classes.formControl}>
                <NativeSelect
                  id="myDropdown"
                  className={classes.selectActivity}
                  onChange={handleFilterChange}
                  inputProps={{
                    name: "timeFilter",
                    id: "time-filter",
                  }}
                >
                  <option value="OVERALL">Overall</option>
                  <option value="TODAY">Today</option>
                  <option value="THIS_WEEK">This Week</option>
                  <option value="THIS_MONTH">This Month</option>
                </NativeSelect>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>

        {/* Top metrics row */}
        <Grid container spacing={2} className={classes.metricsRow}>
          {visibleMetrics.map((m) => {
            const IconComponent = m.icon;
            return (
              <Grid key={m.id} item xs={12} sm={6} md={3}>
                <Card className={`${classes.metricCard} ${m.className}`}>
                  <CardContent style={{ padding: 2 }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box className={classes.metricLeft}>
                        <Typography className={classes.metricLabel}>
                          {m.label}
                        </Typography>
                        <Typography
                          variant="h5"
                          className={classes.metricValue}
                        >
                          {m.value}
                        </Typography>
                        <Typography className={classes.metricMeta}>
                          {m.meta}
                        </Typography>
                      </Box>
                      <Box
                        // Use the custom style for the icon background
                        className={classes.iconBackground}
                        style={{
                          backgroundColor: m.bgColor,
                          border: `1px solid ${m.color}`,
                        }}
                      >
                        <IconComponent
                          // Set the actual icon color to match the surrounding box border color
                          style={{ fontSize: 24, color: m.color }}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Placeholder sections for charts - responsive and ready for integration */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card className={classes.dashboardSection}>
              <Typography variant="subtitle1" gutterBottom>
                Engagement Rate
              </Typography>
              <Box
                height={250}
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="text.secondary"
                position="relative"
              >
                {/* 1. The Gauge Chart */}
                <Box
                  width="100%"
                  maxWidth={300}
                  height={300}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  position="relative"
                  zIndex={2}
                >
                  <EngagementRateGraph data={engagementRateValue} />
                </Box>

                {/* 2. The Icon */}
                <Box
                  position="absolute"
                  left="50%"
                  top="50%"
                  style={{ transform: "translate(-50%, -50%)" }}
                  zIndex={1}
                >
                  <DirectionsRunSharpIcon
                    style={{ fontSize: 50, color: "#a8a8a8ff" }}
                  />
                </Box>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card className={classes.dashboardSection}>
              <Typography variant="subtitle1" gutterBottom>
                Retention Rate
              </Typography>
              <Box
                height={300}
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="text.secondary"
              >
                <RetentionGraph data={retentionRateData} />
              </Box>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={shouldHideRivalry ? 12 : 4}>
            <Card className={classes.dashboardSection}>
              <Typography variant="subtitle1" gutterBottom>
                Quality of Engagement
              </Typography>
              <Box
                height={300}
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="text.secondary"
              >
                <EngagementDoughnutChart data={qualityBreakdown} />
              </Box>
            </Card>
          </Grid>

          {!shouldHideRivalry && (
            <Grid item xs={12} md={8}>
              <Card className={classes.dashboardSection}>
                <Typography variant="subtitle1" gutterBottom>
                  Rivalry Index
                </Typography>
                <Box
                  height={300}
                  display="flex"
                  flexDirection="column"
                  // alignItems="center"
                  // justifyContent="center"
                  color="text.secondary"
                >
                  Measures competitiveness among teams (0 = No rivalry, 5 =
                  Maximum Rivalry)
                  <RivalryIndexTable data={rivalryIndexData} />
                </Box>
              </Card>
            </Grid>
          )}
        </Grid>

        <Grid container spacing={2}>
          {knowledgeSessionData.length > 0 && (
            <Grid item xs={12} md={4}>
              <Card className={classes.dashboardSection}>
                <Typography variant="subtitle1" gutterBottom>
                  Knowledge Sessions
                </Typography>
                <Box height={300} color="text.secondary">
                  <KnowledgeSessionCard data={knowledgeSessionData} />
                </Box>
              </Card>
            </Grid>
          )}

          {!isProgram && (
            <Grid item xs={12} md={knowledgeSessionData.length > 0 ? 8 : 12}>
              <Card className={classes.dashboardSection}>
                <Typography variant="subtitle1" gutterBottom>
                  {challengeParameters === "DISTANCE"
                    ? "Total Distance (KM):"
                    : "Total Steps:"}{" "}
                  {totalDailyActivityValue?.toLocaleString()}
                </Typography>

                <Box
                  height={300}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  color="text.secondary"
                >
                  <BarGraphView
                    data={dailyActivityData}
                    challengeParameters={challengeParameters}
                  />
                </Box>
              </Card>
            </Grid>
          )}
        </Grid>
      </div>
    </div>
  );
}

const styles = (theme) => ({
  mainContainer: {
    fontFamily: '"Karla", sans-serif',
    margin: "5px",
    marginInline: "20px",
    [theme.breakpoints.down("sm")]: { marginInline: "5px" },
  },
  formControl: {
    marginRight: theme.spacing(2),
  },
  breadcrumbs_container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
    "& .breadcrumbs": {
      [theme.breakpoints.down("sm")]: {
        alignSelf: "start",
      },
    },
  },
  selectActivity: {
    // marginTop: theme.spacing(2),
    textAlign: "center",
    color: "#727272",
    fontSize: 18,
  },
  dropdownStyling: {
    padding: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  metricsRow: {
    marginBottom: theme.spacing(1),
  },
  metricCard: {
    display: "flex",
    flexDirection: "column", // allow vertical growth
    alignItems: "stretch",
    justifyContent: "center",
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1.5),
    boxShadow: theme.shadows[1],
    color: theme.palette.text.primary,
    minHeight: 90,
    boxSizing: "border-box", // ensure padding included in size
    overflow: "visible", // avoid hidden overflow on mobile
  },
  iconBackground: {
    // Dimensions for the containing box
    width: 30,
    height: 30,
    // Make it circular
    borderRadius: theme.spacing(1.5), // Use a slight radius for a rounded square look
    // Center the icon inside the box
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    // Set the border to match the icon background color from the screenshot
    padding: 2, // Inner padding to create the border look
  },
  infoCard: {
    // Use rgba to set transparency for the background color (Purple-ish)
    backgroundColor: "rgba(171, 71, 188, 0.1)", // Example: Info primary color with 10% opacity
    // Add a subtle border using the main color for definition
    border: `1px solid #9C27B0`,
    // Set the color of text inside the card (optional, ensures readability)
    // color: theme.palette.info.dark,
  },

  primaryCard: {
    // Use rgba to set transparency for the background color (Blue-ish)
    backgroundColor: "rgba(63, 81, 181, 0.1)", // Example: Primary color with 10% opacity
    border: `1px solid ${theme.palette.info.light}`,
    // color: theme.palette.primary.dark,
  },

  successCard: {
    // Use rgba to set transparency for the background color (Green-ish)
    backgroundColor: "rgba(76, 175, 80, 0.1)", // Example: Success color with 10% opacity
    border: `1px solid ${theme.palette.success.light}`,
    // color: theme.palette.success.dark,
  },

  warningCard: {
    // Use rgba to set transparency for the background color (Yellow-ish)
    backgroundColor: "rgba(242, 255, 0, 0.1)", // Example: Warning color with 10% opacity
    border: `1px solid rgba(169, 175, 52, 0.84)`,
    // color: theme.palette.warning.dark,
  },

  metricLeft: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },

  metricValue: {
    fontWeight: 700,
    lineHeight: 1,
  },

  metricLabel: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },

  metricMeta: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },

  // Placeholder dashboard section(s) below metrics so the user can integrate charts later
  dashboardSection: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    minHeight: 350,
  },
});

export default compose(
  withStyles(styles, { withTheme: true }),
  withRouter,
)(HRDashboardView);
